/**
 * isiZulu: Common words
 *
 * What this file covers: Buttons, statuses, table controls, short notices, countries, phone numbers, password
 * rules and contact attributes used on many pages.
 *
 * Tone: The shortest everyday words. Buttons are verbs (Save, Cancel, Remove). Use one word per idea and never
 * vary it, because these words sit beside every other file's text.
 *
 * isiZulu: Everyday isiZulu as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const common: Catalog = {
  'action.signOut': 'Phuma',
  'action.save': 'Londoloza',
  'action.cancel': 'Khansela',
  'action.create': 'Dala',
  'action.delete': 'Susa',
  'action.search': 'Sesha',
  'common.loading': 'Iyalayisha…',
  'common.language': 'Ulimi',
  'common.unknownError': 'Iphutha elingaziwa',
  'common.name': 'Igama',
  'common.email': 'I-imeyili',
  'common.status': 'Isimo',
  'common.type': 'Uhlobo',
  'common.description': 'Incazelo',
  'common.created': 'Kudalwe',
  'common.updated': 'Kubuyekeziwe',
  'common.actions': 'Izenzo',
  'common.yes': 'Yebo',
  'common.no': 'Cha',
  'common.all': 'Konke',
  'common.none': 'Lutho',
  'common.search': 'Sesha',
  'common.searchPlaceholder': 'Sesha…',
  'common.noResults': 'Ayikho imiphumela.',
  'common.date': 'Usuku',
  'common.amount': 'Inani',
  'common.total': 'Isamba',
  'common.details': 'Imininingwane',
  'common.overview': 'Uhlolojikelele',
  'common.optional': 'Ngokuzikhethela',
  'common.required': 'Kuyadingeka',
  'common.copied': 'Kukopishelwe ebhodini lokunamathisela.',
  'common.enabled': 'Kuvunyelwe',
  'common.disabled': 'Kukhutshaziwe',
  'common.never': 'Akukaze',
  'common.unknown': 'Akwaziwa',
  'common.select': 'Khetha…',
  'common.you': 'Wena',
  'common.notAvailable': 'akutholakali',
  'action.edit': 'Hlela',
  'action.close': 'Vala',
  'action.refresh': 'Vuselela',
  'action.retry': 'Zama futhi',
  'action.back': 'Emuva',
  'action.apply': 'Sebenzisa',
  'action.clear': 'Sula',
  'action.copy': 'Kopisha',
  'action.view': 'Buka',
  'action.confirm': 'Qinisekisa',
  'action.add': 'Engeza',
  'action.remove': 'Susa',
  'action.submit': 'Thumela',
  'action.send': 'Thumela',
  'action.continue': 'Qhubeka',
  'action.next': 'Okulandelayo',
  'action.previous': 'Okwedlule',
  'action.download': 'Landa',
  'action.saveChanges': 'Londoloza izinguquko',
  'action.discard': 'Lahla',
  'action.select': 'Khetha',
  'action.open': 'Vula',
  'action.enable': 'Vumela',
  'action.disable': 'Khubaza',
  'action.activate': 'Yenza kusebenze',
  'action.pause': 'Misa isikhashana',
  'action.start': 'Qala',
  'action.signIn': 'Ngena',
  'action.signUp': 'Bhalisa',
  'action.export': 'Thumela ngaphandle',
  'action.import': 'Ngenisa',
  'action.revoke': 'Hoxisa',
  'action.regenerate': 'Dala kabusha',
  'action.keep': 'Gcina',
  'status.active': 'Kuyasebenza',
  'status.inactive': 'Akusebenzi',
  'status.pending': 'Kulindile',
  'status.approved': 'Kugunyaziwe',
  'status.rejected': 'Kwenqatshiwe',
  'status.failed': 'Kwehlulekile',
  'status.sent': 'Kuthunyelwe',
  'status.delivered': 'Kulethiwe',
  'status.read': 'Kufundiwe',
  'status.draft': 'Uhlaka',
  'status.submitted': 'Kuhanjiswe',
  'status.paused': 'Kumiswe isikhashana',
  'status.running': 'Kuyasebenza',
  'status.completed': 'Kuqediwe',
  'status.cancelled': 'Kukhanseliwe',
  'status.queued': 'Kusemgqeni',
  'status.scheduled': 'Kuhleliwe',
  'status.expired': 'Kuphelelwe yisikhathi',
  'status.revoked': 'Kuhoxisiwe',
  'status.verified': 'Kuqinisekisiwe',
  'status.unverified': 'Akuqinisekisiwe',
  'status.open': 'Kuvuliwe',
  'status.closed': 'Kuvaliwe',
  'status.resolved': 'Kuxazululiwe',
  'status.archived': 'Kugciniwe',
  'status.unknown': 'Akwaziwa',
  'toast.saved': 'Kulondoloziwe',
  'toast.deleted': 'Kususiwe',
  'toast.copied': 'Kukopishiwe',
  'toast.validation': 'Ukuqinisekiswa',
  'toast.saveFailed': 'Ukulondoloza kwehlulekile',
  'toast.deleteFailed': 'Ukususa kwehlulekile',
  'toast.loadFailed': 'Ayikwazanga ukulayisha',
  'notFound.title': 'Ikhasi alitholakali',
  'notFound.backToDashboard': 'Buyela kwideshibhodi',
  'country.za': 'INingizimu Afrika',
  'country.ng': 'INigeria',
  'country.ke': 'IKenya',
  'action.undo': 'Hlehlisa',
  'action.dismiss': 'Cashisa',
  'action.loadMore': 'Layisha okwengeziwe',
  'attributes.group.contact': 'Oxhumana naye',
  'attributes.group.fields': 'Amasimu akho',
  'attributes.group.flow': 'Okuguquguqukayo kokugeleza',
  'attributes.contact.name': 'Igama',
  'attributes.contact.email': 'I-imeyili',
  'attributes.contact.msisdn': 'Inombolo yocingo',
  'attributes.contact.locale': 'Ulimi',
  'attributes.fallbackHint': 'Faka u-| nenani lokubuyela emuva ngemva kwegama, kunoma ubani ongenalutho lapho: contact.name|lapho',
  'attributes.flowFallbackHint': 'Faka u-?? nenani lokubuyela emuva ngemva kwegama, uma kungenalutho: contact.name ?? lapho'
};

export default common;
